import { keepPreviousData, type QueryClient, queryOptions, useQuery } from '@tanstack/react-query';
import { useAccountParams } from '~/hooks/useAccountParams';
import { fetchAnalyticsStatSchedules } from './analyticsStatSchedulesClient';

export const getAnalyticsStatSchedulesQueryOptions = (
  accountId: number,
  accountType: 'client' | 'partner',
  statKey?: string,
) => {
  const listScope = statKey === undefined || statKey === '' ? ('all' as const) : statKey;

  return queryOptions({
    queryKey: ['analytics-stat-schedules', accountId, accountType, listScope],
    queryFn: ({ signal }) =>
      fetchAnalyticsStatSchedules(accountId, accountType, {
        statKey: listScope === 'all' ? undefined : listScope,
        signal,
      }),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
};

type UseAnalyticsStatSchedulesQueryOptions = {
  /** When false, the query does not run (e.g. wait until a saved view’s `statKey` is known). */
  enabled?: boolean;
};

export function useAnalyticsStatSchedulesQuery(
  statKey?: string,
  options?: UseAnalyticsStatSchedulesQueryOptions,
) {
  const params = useAccountParams();
  const accountId = params?.accountId ?? 0;
  const accountType = (params?.accountType ?? 'client') as 'client' | 'partner';
  const enabled = (options?.enabled ?? true) && !!accountId;

  return useQuery({
    ...getAnalyticsStatSchedulesQueryOptions(accountId, accountType, statKey),
    enabled,
    placeholderData: keepPreviousData,
  });
}

/** Call after schedule create/update/delete or pause/resume so hub and per–statKey lists refetch. */
export function invalidateAnalyticsStatScheduleLists(
  queryClient: QueryClient,
  accountId: number,
  accountType: 'client' | 'partner',
) {
  return queryClient.invalidateQueries({
    queryKey: ['analytics-stat-schedules', accountId, accountType],
  });
}
